import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    NotificationAlertDto,
    NotificationAlertsDto,
} from "@station/data/dto/notification-alert.dto";
import {
    NotificationAlertDtoSchema,
    NotificationAlertsDtoSchema,
} from "@station/data/dto/notification-alert.dto";
import {
    NotificationAlert,
    NotificationAlerts,
} from "@station/domain/models/notification-alert.model";
import { injectable } from "inversify";
import type { Result } from "neverthrow";
import { err } from "neverthrow";

@injectable()
export class NotificationAlertMapper {
    map(
        notificationAlertDto: NotificationAlertDto,
    ): Result<NotificationAlert, ModelError<typeof NotificationAlert>> {
        const notificationAlertDtoParsed =
            NotificationAlertDtoSchema.safeParse(notificationAlertDto);

        if (!notificationAlertDtoParsed.success) {
            console.error(notificationAlertDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return NotificationAlert.createWithRecovery({
            id: notificationAlertDto.id,
            title: notificationAlertDto.title,
            body: notificationAlertDto.body,
            read: notificationAlertDto.read,
            createdAt: notificationAlertDto.created_at,
        });
    }

    mapList(notificationAlertsDto: NotificationAlertsDto): NotificationAlerts {
        const notificationAlertsDtoParsed =
            NotificationAlertsDtoSchema.safeParse(notificationAlertsDto);

        if (!notificationAlertsDtoParsed.success) {
            console.error(notificationAlertsDtoParsed.error.issues);
            return NotificationAlerts.empty();
        }

        const notificationAlerts = notificationAlertsDto.mapNotNull(
            (notificationAlertDto) =>
                this.map(notificationAlertDto).match(
                    (notificationAlert) => notificationAlert,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping alert notificationAlerts:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return NotificationAlerts.create({
            notificationAlerts,
            count: notificationAlerts.length,
        }).unwrapOr(NotificationAlerts.empty());
    }
}
